import React, { useState, useEffect } from 'react';

import { useGetAccountInfo } from '@multiversx/sdk-dapp/hooks';
import { BigUIntValue, U32Value } from '@multiversx/sdk-core/out';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, checkboxClasses, FormControlLabel } from '@mui/material';
import axios from 'axios';
import BigNumber from 'bignumber.js/bignumber';
import clsx from 'clsx';
import { Spinner } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

import { CustomButton, CustomModal } from 'components';
import { contracts, ElrondApiUrl } from 'config/config';

import { BigZero, coinNames, coins } from 'helpers/constant';
import { convertEsdtToWei, convertWeiToEsdt, getSymbol, getTokenImg, handleTokenImgError, shortenEsdt } from 'helpers/utils';
import { useInteraction } from 'Interaction';
import styles from './styles.module.scss';
import { CustomInput } from 'components/CustomInput';
import { toast } from 'react-toastify';


export const Staking = () => {
  const [onlyOwnedPool, setOnlyOwnedPool] = useState(false);
  const [onlyYourAssetPool, setOnlyYourAssetPool] = useState(false);
  const [searchKey, setSearchKey] = useState('');

  const [stakings, setStakings] = useState<any[]>([]);
  const [currentStaking, setCurrentStaking] = useState<any>(undefined);
  const [stakeBalance, setStakeBalance] = useState<BigNumber>(BigZero);
  const [stakeDecimals, setStakeDecimals] = useState(0);

  const [stakeAmount, setStakeAmount] = useState<BigNumber>(BigZero);
  const [unstakeAmount, setUnstakeAmount] = useState<BigNumber>(BigZero);
  const [depositAmount, setDepositAmount] = useState<BigNumber>(BigZero);
  const [apr, setApr] = useState<BigNumber>(BigZero);

  const [showStakeModal, setShowStakeModal] = useState(false);
  const [showUnstakeModal, setShowUnstakeModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showAprModal, setShowAprModal] = useState(false);

  const navigate = useNavigate();
  const isMobile = useMediaQuery({
    query: '(max-width: 685px)'
  });

  const { fetching, stakingInfos, pairInfos, callMethod } = useInteraction();
  const { account: {address: userAddress, balance: userBalance} } = useGetAccountInfo();

  useEffect(() => {
    (async () => {
      if(currentStaking && userAddress) {
        try {
          const { data } = await axios.get(`${ElrondApiUrl}/accounts/${userAddress}/tokens/${currentStaking?.stake_token_id}`);

          const balance = new BigNumber(data.balance || 0);
          setStakeBalance(convertWeiToEsdt(balance, data.decimals));
          setStakeDecimals(data.decimals);
        } catch (error) {
            console.log(error);
        }
      }
    })();
  }, [currentStaking]);

  useEffect(() => {
    findValidStakings();
  }, [stakingInfos, pairInfos, onlyOwnedPool, onlyYourAssetPool]);

  // console.log(stakingInfos);

  useEffect(() => {
    const timer = setTimeout(async () => {
      findValidStakings();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchKey]);

  const findValidStakings = () => {
    const stakingData = [];

    for(const staking of stakingInfos) {
      let valid = 0;

      if(!onlyOwnedPool && !onlyYourAssetPool) {
        valid += 1;
      }

      if(onlyOwnedPool && userAddress && staking.owner.toString() == userAddress) {
        valid += 1;
      }

      if(onlyYourAssetPool && userAddress && (BigZero.lt(staking?.user_stake_amount) || BigZero.lt(staking?.user_reward_amount))) {
        valid += 1;
      }

      if(!valid) continue;

      if(searchKey) {
        if(
          !staking.stake_token_id.toLowerCase().includes(searchKey.toLocaleLowerCase())
        ) continue;
      }

      const stake_token_id = staking.stake_token_id;

      let stake_token_img = '';
      if(coinNames.includes(stake_token_id)) {
        stake_token_img = coins[stake_token_id];
      } else {
        stake_token_img = coins['unknown'];
      }

      const stake_token_symbol = getSymbol(stake_token_id);

      stakingData.push({
        ...staking,
        stake_token_img,
        stake_token_symbol
      });
    }

    setStakings(stakingData);
  };

  // console.log(stakings);

  const stake = async() => {
    if(
      BigZero.gte(stakeAmount) ||
      !userAddress
    ) return;

    try {
      await callMethod({
          contract: contracts.Staking,
          method: 'userStake',
          args: [
            new U32Value(currentStaking.pool_id),
          ],
          fts: [
              {
                  token: currentStaking.stake_token_id,
                  amount: stakeAmount,
                  decimals: currentStaking.stake_token_decimals.toNumber()
              },
          ],
      });

      setShowStakeModal(false);
    } catch(error: any) {
        console.log(error);
    }
  };

  const unstake = async() => {
    if(
      BigZero.gte(unstakeAmount) ||
      !userAddress
    ) return;

    try {
      await callMethod({
          contract: contracts.Staking,
          method: 'userUnstake',
          args: [
            new U32Value(currentStaking.pool_id),
            new BigUIntValue(convertEsdtToWei(unstakeAmount, currentStaking?.stake_token_decimals.toNumber())),
          ],
      });

      setShowUnstakeModal(false);
    } catch(error: any) {
        console.log(error);
    }
  };

  const claimReward = async(staking: any) => {
    if(
      BigZero.gte(staking.user_reward_amount) ||
      !userAddress
    ) return;

    if(staking.user_reward_amount.gt(staking.reward_pool_size)) {
      toast.error('There is no enough reward in pool.');
      
      return;
    }

    try {
      await callMethod({
          contract: contracts.Staking,
          method: 'userClaim',
          args: [
            new U32Value(staking.pool_id),
          ],
      });
    } catch(error: any) {
        console.log(error);
    }
  };

  const depositReward = async() => {
    if(
      BigZero.gte(depositAmount) ||
      !userAddress
    ) return;

    try {
        await callMethod({
            contract: contracts.Staking,
            method: 'depositReward',
            args: [
                new U32Value(currentStaking.pool_id),
            ],
            fts: [
                {
                    token: currentStaking.stake_token_id,
                    amount: depositAmount,
                    decimals: currentStaking.stake_token_decimals.toNumber()
                },
            ],
        });

        setShowDepositModal(false);
    } catch(error: any) {
        console.log(error);
    }
  };

  const changeApr = async() => {
    if(
      BigZero.gte(apr) ||
      !userAddress
    ) return;

    try {
        await callMethod({
            contract: contracts.Staking,
            method: 'changeAnnualReward',
            args: [
                new U32Value(currentStaking.pool_id),
                new BigUIntValue(apr.multipliedBy(100).toFixed()),
            ],
        });

        setShowAprModal(false);
    } catch(error: any) {
        console.log(error);
    }
  };

  return (
    <>
      <div className='d-flex flex-column flex-fill align-items-center justify-content-start'>
        <div className={clsx(styles.card, 'card shadow-sm mx-5')}>
            <div className={styles.cardBody}>
              <div className={clsx(styles.header, 'd-flex justify-content-between align-items-center w-100')}>
                <div className='d-flex justify-content-between align-items-center'>
                  {
                    isMobile ? (
                      <h4>STAKING</h4>
                    ) : (
                      <h2>STAKING</h2>
                    )
                  }
                </div>
              </div>
              <div className='d-flex w-100 justify-content-end text-white'>
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: 'white',
                        [`&.${checkboxClasses.checked}`]: {
                          color: '#ff9d19',
                        },
                      }}
                      checked={onlyOwnedPool}
                      onChange={(e, checked) => setOnlyOwnedPool(checked)}
                    />
                  }
                  label="Your Pools"
                />

                <FormControlLabel
                  className='ml-2'
                  control={
                    <Checkbox
                      sx={{
                        color: 'white',
                        [`&.${checkboxClasses.checked}`]: {
                          color: '#ff9d19',
                        },
                      }}
                      checked={onlyYourAssetPool}
                      onChange={(e, checked) => setOnlyYourAssetPool(checked)}
                    />
                  }
                  label="Your Assets"
                />
                {/* <span>TVL: $123.25</span>  */}
              </div>

              <div className='w-100 d-flex justify-content-between align-items-center'>
                <input
                    className={clsx(styles.search, 'flex-grow-1 my-4 mr-2')}
                    type='text'
                    placeholder='Search Pool'
                    value={searchKey}
                    onChange={(e) => setSearchKey(e.target.value)}
                />
                <CustomButton
                  className={styles.createButton}
                  onClick={() => {
                    navigate('/staking/create');
                  }}
                >
                  {
                    isMobile ? ' Create' : (
                      <>
                        <i className='fa-solid fa-plus'></i>
                        {' '}Create New Staking
                      </>
                    )
                  }
                </CustomButton>
              </div>
              {
                !isMobile && (
                  <div className={clsx(styles.listHeader, 'd-flex justify-content-between align-items-center row')}>
                    <div className='col-1'>#</div>
                    <div className='col-3'>Stake Token</div>
                    <div className='col-5 text-center'>Total Stake</div>
                    <div className='col-3 text-center'>APR</div>
                  </div>
                )
              }
              <div className={styles.list}>
                {
                  stakings.length > 0 &&
                    stakings.map((staking, index) => {
                      return (
                        <Accordion
                          key={index}
                          className={clsx(styles.listItem)}
                        >
                          <AccordionSummary
                            sx={{ px: 0 }}
                            expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                          >
                            {
                              !isMobile ? (
                                <div className='col-sm-1'>
                                  {staking?.pool_id.toNumber() + 1}
                                </div>
                              ) : (
                                <></>
                              )
                            }
                            <div className='col-5 col-sm-3 d-flex align-items-center'>
                              <img src={getTokenImg(staking?.stake_token_id)} onError={handleTokenImgError}  alt='stake' width={24} />
                              <span className='bold ml-2'>{staking?.stake_token_symbol}</span>
                            </div>
                              {
                                !isMobile && (
                                  <div className='col-3 col-sm-5 d-flex align-items-center justify-content-center'>
                                    <span className='bold ml-2'>{shortenEsdt(convertWeiToEsdt(staking?.total_stake_amount, staking?.stake_token_decimals.toNumber()))} {staking?.stake_token_symbol}</span>
                                  </div>
                                )
                              }
                            <div className='col-7 col-sm-3 d-flex align-items-center justify-content-center'>{shortenEsdt(new BigNumber(staking.apr.toString()).dividedBy(100))}%</div>
                          </AccordionSummary>
                          <AccordionDetails className='row'>
                            {
                              isMobile && (
                                <div className='col-12 d-flex flex-column'>
                                  <span className='text-secondary'>
                                    APR
                                  </span>
                                  <span>
                                    <img src={getTokenImg(staking?.stake_token_id)} onError={handleTokenImgError}  alt='' width={20} /> {shortenEsdt(new BigNumber(staking.apr.toString()).dividedBy(100))}%
                                    <br />
                                    <span className='text-secondary'>
                                      {staking?.stake_token_id}
                                    </span>
                                  </span>
                                </div>

                              )
                            }
                            <div className='col-12 col-sm-6 col-md-3 d-flex flex-column mt-4 mt-sm-0'>
                              <span className='text-secondary'>
                                Total Staked Amount
                              </span>
                              <span>
                                <img src={getTokenImg(staking?.stake_token_id)} onError={handleTokenImgError} alt='' width={20} /> {shortenEsdt(convertWeiToEsdt(staking?.total_stake_amount, staking?.stake_token_decimals.toNumber()))} {staking?.stake_token_symbol}
                              </span>
                              <span className='text-secondary'>
                                {staking?.stake_token_id}
                              </span>
                            </div>

                            <div className='col-12 col-sm-6 col-md-3 d-flex flex-column mt-4 mt-sm-0'>
                              <span className='text-secondary'>
                                Reward Pool
                              </span>
                              <span>
                                <img src={getTokenImg(staking?.stake_token_id)} onError={handleTokenImgError}  alt='' width={20} /> {shortenEsdt(convertWeiToEsdt(staking?.reward_pool_size, staking?.stake_token_decimals.toNumber()))} {staking?.stake_token_symbol}
                              </span>
                              <span className='text-secondary'>
                                {staking?.stake_token_id}
                              </span>
                            </div>

                            <div className='col-12 col-sm-6 col-md-3 d-flex flex-column mt-4 mt-sm-0'>
                              <span className='text-secondary'>
                                Staker Count
                              </span>
                              <span>
                                {staking?.staker_count.toNumber()}
                              </span>
                            </div>

                            <div className='col-12 col-sm-6 col-md-3 d-flex flex-column mt-4 mt-sm-0'>
                              <span className='text-secondary'>
                                Your Staked Amount
                              </span>
                              <span>
                              <img src={getTokenImg(staking?.stake_token_id)} onError={handleTokenImgError}  alt='' width={20} /> {shortenEsdt(convertWeiToEsdt(staking?.user_stake_amount, staking?.stake_token_decimals.toNumber()))} {staking?.stake_token_symbol}
                              </span>
                              <span className='text-secondary'>
                                Pending Reward
                              </span>
                              <span>
                              <img src={getTokenImg(staking?.stake_token_id)} onError={handleTokenImgError}  alt='' width={20} /> {shortenEsdt(convertWeiToEsdt(staking?.user_reward_amount, staking?.stake_token_decimals.toNumber()))} {staking?.stake_token_symbol}
                              </span>
                            </div>

                            <div className='col-4 text-center mt-4 p-0'>
                              <CustomButton
                                className={styles.button}
                                onClick={() => {
                                  if(userAddress) {
                                    setCurrentStaking(staking);
                                    setShowStakeModal(true);
                                  }
                                }}
                              >
                                Stake
                              </CustomButton>
                            </div>

                            <div className='col-4 text-center mt-4 p-0'>
                              <CustomButton
                                className={styles.button}
                                onClick={() => {
                                  if(userAddress) {
                                    setCurrentStaking(staking);
                                    setShowUnstakeModal(true);
                                  }
                                }}
                              >
                                Unstake
                              </CustomButton>
                            </div>

                            <div className='col-4 text-center mt-4 p-0'>
                              <CustomButton
                                className={styles.button}
                                onClick={() => {
                                  if(!userAddress) {
                                    return;
                                  }

                                  if(BigZero.lt(staking.user_reward_amount)) {
                                    claimReward(staking);
                                  } else {
                                    toast.info('No reward to claim!');
                                  }
                                }}
                              >
                                Claim
                              </CustomButton>
                            </div>

                            {
                              (userAddress && userAddress == staking.owner.toString()) ? (
                                <>
                                  <span className='col-12 text-center mt-4 text-white'>
                                    Staking Management
                                  </span>
                                  <div className='col-12 col-sm-6 text-center mt-2 p-0'>
                                    <CustomButton
                                      className={clsx(styles.button, styles.manageBtn)}
                                      onClick={() => {
                                        if(!userAddress) {
                                          return;
                                        }

                                        setCurrentStaking(staking);
                                        setShowDepositModal(true);
                                      }}
                                    >
                                      Deposit Reward
                                    </CustomButton>
                                  </div>

                                  <div className='col-12 col-sm-6 text-center mt-2 p-0'>
                                    <CustomButton
                                      className={clsx(styles.button, styles.manageBtn)}
                                      onClick={() => {
                                        if(!userAddress) {
                                          return;
                                        }

                                        setCurrentStaking(staking);
                                        setShowAprModal(true);
                                      }}
                                    >
                                      Change Annual Reward
                                    </CustomButton>
                                  </div>
                                </>
                              ) : (
                                <></>
                              )
                            }
                          </AccordionDetails>
                        </Accordion>
                      );
                    })
                  }
              </div>
              {
                stakings.length === 0 && (
                  fetching ? (
                    <Spinner className='text-white mt-5' animation="border" />
                  ) : (
                    <h3 className='text-white mt-5'>No Available Pools</h3>
                  )
                )
              }
            </div>
        </div>
      </div>

      <CustomModal
        title='Stake'
        show={showStakeModal}
        onClose={() => setShowStakeModal(false)}
      >
        <div className='d-flex flex-column justify-content-center align-items-start'>
          <h6 className='text-white text-left mt-4'>Stake Token Amount</h6>
          <span className='text-info'>{currentStaking?.stake_token_id}</span>
          <span className='text-white'>Balance: {shortenEsdt(stakeBalance)}</span>
          <CustomInput
              className={clsx(styles.import, 'flex-grow-1 w-100')}
              placeholder='Stake Token Amount'
              value={stakeAmount}
              maxValue={stakeBalance}
              setAmount={(value: BigNumber) => {
                if(value.lte(stakeBalance)) {
                  setStakeAmount(value);
                }
              }}
          />
          <CustomButton
              className={styles.applyButton}
              onClick={() => stake()}
          >
              Stake
          </CustomButton>
        </div>
      </CustomModal>

      <CustomModal
        title='Unstake'
        show={showUnstakeModal}
        onClose={() => setShowUnstakeModal(false)}
      >
        <div className='d-flex flex-column justify-content-center align-items-start'>
          <h6 className='text-white text-left mt-4'>Unstake Token Amount</h6>
          <span className='text-info'>{currentStaking?.stake_token_id}</span>
          <span className='text-white'>Your staked Amount: {shortenEsdt(convertWeiToEsdt(currentStaking?.user_stake_amount, currentStaking?.stake_token_decimals.toNumber()))}</span>
          <CustomInput
              className={clsx(styles.import, 'flex-grow-1 w-100')}
              placeholder='Unstake Token Amount'
              value={unstakeAmount}
              maxValue={convertWeiToEsdt(currentStaking?.user_stake_amount, currentStaking?.stake_token_decimals.toNumber())}
              setAmount={(value: BigNumber) => {
                if(value.lte(convertWeiToEsdt(currentStaking?.user_stake_amount, currentStaking?.stake_token_decimals.toNumber()))) {
                  setUnstakeAmount(value);
                }
              }}
          />
          <CustomButton
              className={styles.applyButton}
              onClick={() => unstake()}
          >
              Unstake
          </CustomButton>
        </div>
      </CustomModal>

      <CustomModal
        title='Deposit Reward'
        show={showDepositModal}
        onClose={() => setShowDepositModal(false)}
      >
        <div className='d-flex flex-column justify-content-center align-items-start'>
          <h6 className='text-white text-left mt-4'>Reward Token Amount</h6>
          <span className='text-info'>{currentStaking?.stake_token_id}</span>
          <span className='text-white'>Balance: {shortenEsdt(stakeBalance)}</span>
          <CustomInput
              className={clsx(styles.import, 'flex-grow-1 w-100')}
              placeholder='Reward Token Amount'
              value={depositAmount}
              maxValue={stakeBalance}
              setAmount={(value: BigNumber) => {
                if(value.lte(stakeBalance)) {
                  setDepositAmount(value);
                }
              }}
          />
          <CustomButton
              className={styles.applyButton}
              onClick={() => depositReward()}
          >
              Deposit
          </CustomButton>
        </div>
      </CustomModal>

      <CustomModal
        title='Change Annual Reward'
        show={showAprModal}
        onClose={() => setShowAprModal(false)}
      >
        <div className='d-flex flex-column justify-content-center align-items-start'>
          <h6 className='text-white text-left mt-4'>APR</h6>
          <span className='text-info'>{currentStaking?.stake_token_id}</span>
          <CustomInput
              className={clsx(styles.import, 'w-100 px-3')}
              leftAlign={true}
              noMax={true}
              placeholder={'%'}
              value={apr}
              setAmount={(newAmount: BigNumber) => {
                  setApr(newAmount);
              }}
          />
          <CustomButton
              className={styles.applyButton}
              onClick={() => changeApr()}
          >
              Change
          </CustomButton>
        </div>
      </CustomModal>
    </>
  );
};
